import { cn } from '@/utils/cn';

import { NODES, PATH, VIEWBOX } from './journeyGeometry';

/**
 * The curve the three "How Wonder Works" milestones sit on.
 *
 * Everything geometric lives in one SVG so the path, the nodes and the dotted
 * droplines scale as a unit -- placing the nodes as separate HTML would mean
 * re-deriving three positions every time the curve moved. The step text is
 * positioned against the same viewBox through `atViewBox`.
 *
 * The line is drawn twice: once wide and blurred for the bloom, once thin and
 * bright over it. One stroke wearing a CSS drop-shadow gives a hard edge with a
 * halo; two strokes read as light.
 *
 * `pathLength="1"` rescales the path's own length to 1 unit, so the dash
 * geometry is already normalised and the draw-on animation is a plain 1 -> 0
 * on `strokeDashoffset`. Without it the section would have to measure the path
 * at runtime and re-measure on every resize.
 *
 * The `js-` classes are animation handles for HowWonderWorks. They carry no
 * styling, and the section is the only thing that may bind to them.
 */
export function JourneyCurve({ className }) {
  return (
    <svg
      aria-hidden="true"
      // Node 3 sits its own radius below the top edge and its bloom reaches
      // past it; clipped, the dot arrives with a flat top.
      className={cn('overflow-visible', className)}
      viewBox={`0 0 ${VIEWBOX.width} ${VIEWBOX.height}`}
      fill="none"
    >
      <defs>
        <filter id="journey-bloom" x="-10%" y="-30%" width="120%" height="160%">
          <feGaussianBlur stdDeviation="6" />
        </filter>
        {/*
          The line fades in from nothing on the left and out to nothing on the
          right, the way the frame draws it -- a stroke that simply stops reads
          as cut off. objectBoundingBox units, so the ramp spans the path's own
          extent whatever the viewBox is.

          The fade-in is complete by 0.07, about halfway out to node 1 at 0.13,
          and that half matters. A first pass ended the ramp at node 1 itself,
          which put the whole of the arrival leg inside it: on landing, the
          line the reader is shown running out to the first dot was a wisp
          that only reached full strength at the dot. The fade-out starts past
          node 3, so wherever a dot sits the line is solid.
        */}
        <linearGradient id="journey-fade" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#C4B0FA" stopOpacity="0" />
          <stop offset="0.07" stopColor="#C4B0FA" stopOpacity="1" />
          <stop offset="0.88" stopColor="#C4B0FA" stopOpacity="1" />
          <stop offset="1" stopColor="#C4B0FA" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="journey-fade-glow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="#A88CF5" stopOpacity="0" />
          <stop offset="0.07" stopColor="#A88CF5" stopOpacity="1" />
          <stop offset="0.88" stopColor="#A88CF5" stopOpacity="1" />
          <stop offset="1" stopColor="#A88CF5" stopOpacity="0" />
        </linearGradient>
      </defs>

      <path
        className="js-wave-glow"
        d={PATH}
        pathLength="1"
        strokeDasharray="1"
        stroke="url(#journey-fade-glow)"
        strokeWidth="6"
        opacity="0.45"
        filter="url(#journey-bloom)"
      />
      <path
        className="js-wave"
        d={PATH}
        pathLength="1"
        strokeDasharray="1"
        stroke="url(#journey-fade)"
        strokeWidth="1.5"
        opacity="0.9"
      />

      {NODES.map((node) => (
        <g key={node.x}>
          <line
            className="js-drop"
            x1={node.x}
            y1={node.y + 14}
            x2={node.x}
            y2={node.dropTo}
            stroke="#C4B0FA"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="0.5 7"
            opacity="0.45"
          />
          {/* The energy that the arriving line dumps into the node. It lives at
              zero opacity until the timeline expands it, and is never visible
              in the static render. */}
          <circle
            className="js-pulse"
            cx={node.x}
            cy={node.y}
            r="9"
            fill="none"
            stroke="#C4B0FA"
            strokeWidth="2"
            opacity="0"
          />
          <g className="js-node">
            <circle cx={node.x} cy={node.y} r="11" fill="#621092" />
            <circle cx={node.x} cy={node.y} r="9" fill="none" stroke="#FFFFFF" strokeWidth="2.5" />
          </g>
        </g>
      ))}
    </svg>
  );
}
